<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import { sessionStore } from '@/stores/session'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Checkbox from 'primevue/checkbox'
import Button from 'primevue/button'
import { getErrorMessage } from '@/utils/error'

import educationImage from '@/images/logo.jpg'

const email = ref('')
const password = ref('')
const remember = ref(true)
const session = sessionStore()
const errorMessage = ref('')
const { t } = useI18n()
const route = useRoute()
const successMessage = route.query.registered ? t('login.registrationSuccess') : ''

async function handleLogin() {
  errorMessage.value = ''

  try {
    await session.login.submit({
      usr: email.value.trim(),
      pwd: password.value,
      remember_me: remember.value,
    })
  } catch (error) {
    errorMessage.value = getErrorMessage(error, t('login.signInError'))
  }
}
</script>

<template>
  <main class="login-page">
    <section class="branding-panel">
      <div class="branding-content">
        <img
          :src="educationImage"
          :alt="t('login.illustrationAlt')"
          class="education-image"
        />

        <div class="branding-copy">
          <h1>{{ t('login.empoweringEducation') }}</h1>
          <p>
            {{ t('login.brandingMessage') }}
          </p>
        </div>
      </div>
    </section>

    <section
      class="login-panel"
      aria-labelledby="login-title"
    >
      <div class="login-content">
        <div class="brand-name">Retiora</div>

        <div class="login-heading">
          <h2 id="login-title">{{ t('login.welcomeBack') }}</h2>
          <p>{{ t('login.welcomeMessage') }}</p>
        </div>

        <form
          class="login-form"
          @submit.prevent="handleLogin"
        >
          <div class="field">
            <label for="email">{{ t('login.emailOrUsername') }}</label>

            <InputText
              id="email"
              v-model="email"
              fluid
              autocomplete="username"
              placeholder="your_email@gmail.com"
            />
          </div>

          <div class="field">
            <label for="password">{{ t('login.password') }}</label>

            <Password
              v-model="password"
              input-id="password"
              placeholder="••••••••"
              :feedback="false"
              toggle-mask
              fluid
              :input-props="{ autocomplete: 'current-password' }"
            />
          </div>

          <div class="form-options">
            <div class="remember-row">
              <Checkbox
                v-model="remember"
                input-id="remember"
                :binary="true"
              />

              <label for="remember">{{ t('login.rememberMe') }}</label>
            </div>

            <a
              href="#"
              class="forgot-password"
            >
              {{ t('login.forgotPassword') }}
            </a>
          </div>

          <Button
            type="submit"
            :label="t('login.signIn')"
            fluid
            :loading="session.login.loading"
            :disabled="session.login.loading"
          />

          <p
            v-if="errorMessage"
            class="login-error"
            role="alert"
          >
            {{ errorMessage }}
          </p>

          <p
            v-if="successMessage"
            class="login-success"
            role="status"
          >
            {{ successMessage }}
          </p>
        </form>

        <p class="request-access">
          {{ t('login.noAccount') }}
          <RouterLink :to="{ name: 'register' }">{{ t('login.register') }}</RouterLink>
        </p>
      </div>
    </section>
  </main>
</template>

<style scoped>
.login-page {
  min-height: 100vh;
  display: grid;
  grid-template-columns: 1fr 1.15fr;
  background: #f7f8ff;
}

.branding-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
  background:
    radial-gradient(circle at 40% 55%, rgba(204, 243, 239, 0.9), transparent 34%),
    linear-gradient(180deg, #e8f0ff 0%, #dfeaff 100%);
}

.branding-content {
  width: 100%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.education-image {
  width: 100%;
  max-width: 550px;
  aspect-ratio: 1.5;
  object-fit: cover;
  background: #f8f7f1;
  box-shadow: 0 28px 60px rgba(21, 40, 80, 0.14);
}

.branding-copy {
  margin-top: 42px;
  text-align: center;
}

.branding-copy h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.15;
  font-weight: 700;
  letter-spacing: -0.03em;
  color: #0f172a;
}

.branding-copy p {
  max-width: 520px;
  margin: 16px auto 0;
  font-size: 20px;
  line-height: 1.55;
  color: #334155;
}

.login-panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 64px;
}

.login-content {
  width: 100%;
  max-width: 490px;
}

.brand-name {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  margin-bottom: 28px;
  padding: 0 6px;

  font-size: 14px;
  font-weight: 500;

  color: #4646d8;
  background: rgba(241, 245, 249, 0.75);
}

.login-heading {
  margin-bottom: 44px;
}

.login-heading h2 {
  margin: 0;
  font-size: 48px;
  line-height: 1.05;
  font-weight: 700;
  letter-spacing: -0.045em;
  color: #0f172a;
}

.login-heading p {
  margin: 12px 0 0;
  font-size: 20px;
  color: #343949;
}

.login-form {
  display: flex;
  flex-direction: column;
  gap: 26px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.field label {
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.08em;
  color: #171827;
}

.form-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.remember-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.remember-row label {
  font-size: 15px;
  color: #343949;
  cursor: pointer;
}

.forgot-password,
.request-access a {
  color: #3531d7;
  font-weight: 600;
  text-decoration: none;
  letter-spacing: 0.04em;
}

.forgot-password:hover,
.request-access a:hover {
  text-decoration: underline;
}

.request-access {
  margin: 44px 0 0;
  text-align: center;

  font-size: 14px;
  color: #343949;
}

.login-error {
  margin: -8px 0 0;
  color: #b91c1c;
  font-size: 14px;
}

.login-success {
  margin: -8px 0 0;
  color: #15803d;
  font-size: 14px;
}

@media (max-width: 1000px) {
  .login-page {
    grid-template-columns: 1fr;
  }

  .branding-panel {
    display: none;
  }

  .login-panel {
    min-height: 100vh;
    padding: 40px 24px;
  }

  .login-content {
    max-width: 480px;
  }
}

@media (max-width: 640px) {
  .login-panel {
    align-items: flex-start;
    padding: 48px 20px;
  }

  .brand-name {
    margin-bottom: 24px;
  }

  .login-heading {
    margin-bottom: 36px;
  }

  .login-heading h2 {
    font-size: 40px;
  }

  .login-heading p {
    font-size: 17px;
  }

  .form-options {
    align-items: flex-start;
  }
}
</style>
